import { getCorrectFilePath } from "@/helpers/new/getCorrectFilePath"
import { Image, Modal } from "antd"
import s from './FileView.module.css'
import AudioPlayer from "@UI/AudioPlayer"
import { useEffect, useState } from "react"
import VideoPlayer from "@UI/VideoPlayer"
import { getAttachmentType } from '../../helpers/attachments'

const getAudioDuration = (file) => {
    const duration = Number(
        file?.durationSec
        ?? file?.duration_sec
        ?? file?.durationSeconds
        ?? file?.duration_seconds
        ?? file?.audioDuration
        ?? file?.audio_duration
        ?? file?.metadata?.duration
        ?? file?.meta?.duration
        ?? 0,
    );

    if (!Number.isFinite(duration) || duration <= 0) return undefined;
    return duration > 1000 ? duration / 1000 : duration;
}

const getImageKey = (file) => file?.id ?? file?.path ?? file?.name

const MessageImage = ({ file, width, height, borderRadius, onClick, tiled = false }) => {
    const [aspectRatio, setAspectRatio] = useState(4 / 3)
    const src = getCorrectFilePath(file.path, '')
    const isHeic = /^image\/hei[cf]$/i.test(String(file.type || file.mime_type || ''))
        || /\.(heic|heif)(?:[?#]|$)/i.test(String(file.original || file.original_name || file.name || file.path || ''))
    const [preview, setPreview] = useState(null)
    const [failedSrc, setFailedSrc] = useState(null)

    useEffect(() => {
        if (!isHeic) return

        let cancelled = false
        let objectUrl
        const convert = async () => {
            try {
                const response = await fetch(src)
                if (!response.ok) throw new Error('Не удалось загрузить изображение')
                const { heicTo } = await import('heic-to')
                const jpeg = await heicTo({ blob: await response.blob(), type: 'image/jpeg', quality: 0.85 })
                if (!cancelled) {
                    objectUrl = URL.createObjectURL(jpeg)
                    setPreview({ original: src, url: objectUrl })
                }
            } catch (error) {
                if (!cancelled) setFailedSrc(src)
            }
        }
        convert()

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [isHeic, src])

    const imageSrc = !isHeic || failedSrc === src ? src : preview?.original === src ? preview.url : null

    if (!imageSrc) {
        return <div className={s.imageLoading} style={{ width, height, aspectRatio: height ? undefined : tiled ? 1 : '4 / 3', borderRadius }} aria-label="Загрузка изображения" />
    }

    if (height) {
        return (
            <Image
                width={width}
                height={height}
                src={imageSrc}
                style={{
                    borderRadius: borderRadius,
                    objectFit: 'contain',
                    maxHeight: height,
                    maxWidth: '100%',
                }}
                onClick={onClick}
            />
        )
    }

    return (
        <div
            className={s.imageFrame}
            style={{
                width,
                maxWidth: '100%',
                aspectRatio: tiled ? 1 : Math.max(aspectRatio, 0.65),
                borderRadius,
            }}
        >
            <Image
                width="100%"
                height="100%"
                src={imageSrc}
                style={{
                    borderRadius,
                    objectFit: tiled ? 'cover' : 'contain',
                }}
                alt={file.original || file.original_name || file.name || 'Фото'}
                onLoad={(event) => {
                    const image = event.currentTarget
                    if (image?.naturalWidth && image?.naturalHeight) {
                        setAspectRatio(image.naturalWidth / image.naturalHeight)
                    }
                }}
                onClick={onClick}
            />
        </div>
    )
}

export const FileView = ({files, isAutor = false, width = 200, height, borderRadius = '14px'}) => {
    const [openedVideo, setOpenedVideo] = useState(null);
    const isAlbum = !height && Array.isArray(files) && files.filter((file) => ['image', 'video'].includes(getAttachmentType(file))).length > 1;

    const nadleClick = (e) => {
        e.stopPropagation()
    }
    
    if(!Array.isArray(files)) return <></>;

    return(
        <div onClick={nadleClick} style={{alignItems: !isAlbum && isAutor ? 'flex-end' : undefined}} className={`${s.messageImages} ${isAlbum ? s.album : ''}`}>
            <Image.PreviewGroup>
            {
                files?.map((file, i) => {
                    const fileType = getAttachmentType(file);

                    if(fileType.includes('image')){
                        return (
                                <MessageImage
                                    key={getImageKey(file) ?? i}
                                    file={file}
                                    width={isAlbum ? '100%' : width}
                                    height={height}
                                    borderRadius={borderRadius}
                                    tiled={isAlbum}
                                    onClick={(e) => nadleClick(e)}
                                />
                        )
                    }

                    if (fileType === 'video') {
                        return (
                            <button type="button" key={getImageKey(file) ?? i} className={s.videoTile}
                                style={{ width: isAlbum ? '100%' : width, height, borderRadius, aspectRatio: height ? undefined : isAlbum ? 1 : '4 / 3' }}
                                aria-label={`Смотреть видео ${file.original || file.original_name || ''}`}
                                onClick={() => setOpenedVideo(file)}>
                                <video src={getCorrectFilePath(file.path)} preload="metadata" muted playsInline />
                                <span className={s.playVideo} aria-hidden="true">▶</span>
                            </button>
                        );
                    }
                    
                    if(fileType.includes('audio')){
                        return (
                            <div key={file.id} className={s.audioMessage} onClick={(e) => nadleClick(e)}>
                                <AudioPlayer audioTrack={getCorrectFilePath(file.path)} durationSec={getAudioDuration(file)} />
                            </div>
                        )
                    }
                    return (
                        <a 
                                onClick={(e) =>nadleClick(e)}

                                className={s.messageLink} href={getCorrectFilePath(file.path)}
                                target={'_blank'}
                                rel={'noopener noreferrer'}
                                key={file.id}
                            >
                                {file?.original || file?.original_name || file?.name || 'Скачать файл'}
                        </a>
                    )
                })
            }
            </Image.PreviewGroup>
            <Modal open={!!openedVideo} onCancel={() => setOpenedVideo(null)} footer={null} width={800} centered destroyOnClose title="Видео">
                {openedVideo && <>
                    {/* Метка нужна доку чатов: полноэкранный режим этого плеера не должен
                        перестраивать док, иначе чат с модалкой размонтируется и видео закроется */}
                    <div data-chat-media-viewer="true">
                        <VideoPlayer key={openedVideo.path} src={getCorrectFilePath(openedVideo.path)} />
                    </div>
                    <a className={s.messageLink} href={getCorrectFilePath(openedVideo.path)} target="_blank" rel="noopener noreferrer">Открыть оригинал видео</a>
                </>}
            </Modal>
        </div>
    )
}
